import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

import { db, storage } from "../../firebase";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../../config/featureFlags";

const functions = getFunctions(undefined, "us-central1");

const MIN_WITHDRAW_TOKENS = 1000;

const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
  gap: 16,
};

const CARD = {
  border: "1px solid #253654",
  borderRadius: 14,
  background: "linear-gradient(180deg, #12213a, #101b30)",
  padding: 18,
  minWidth: 0,
};

const SECTION_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#7d8fb5",
  marginBottom: 10,
  paddingBottom: 6,
  borderBottom: "1px solid #1e2f4d",
};

const ROW = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  gap: "8px 14px",
  fontSize: 13,
  alignItems: "start",
  marginBottom: 6,
};

const LABEL = { color: "#9fb2d8" };
const VALUE = { color: "#e8eefc", wordBreak: "break-word" };
const MONO = {
  ...VALUE,
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: 12,
};

function ResolvedAvatar({ raw, imgStyle, gender }) {
  const fb = defaultAvatarByGender(gender);
  const [src, setSrc] = useState(fb);

  useEffect(() => {
    let cancelled = false;
    const fallback = defaultAvatarByGender(gender);
    const v = String(raw || "").trim();
    if (!v) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    if (/^https?:\/\//i.test(v)) {
      setSrc(v);
      return () => {
        cancelled = true;
      };
    }
    let path = v;
    if (v.startsWith("gs://")) {
      const m = v.match(/^gs:\/\/[^/]+\/(.+)$/);
      path = m ? m[1] : "";
    }
    if (!path) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const url = await getDownloadURL(storageRef(storage, path));
        if (!cancelled) setSrc(url);
      } catch (e) {
        console.error("ResolvedAvatar getDownloadURL:", path, e);
        if (!cancelled) setSrc(fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, gender]);

  return (
    <img
      src={src}
      alt=""
      style={imgStyle}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = defaultAvatarByGender(gender);
      }}
    />
  );
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function pickPhotoUrl(user) {
  const raw = user?.profileImage || user?.photoURL || "";
  return typeof raw === "string" ? raw.trim() : "";
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

export default function WithdrawEligibilityPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingUid, setWorkingUid] = useState(null);
  const [feedbackByUid, setFeedbackByUid] = useState({});
  const [rejectionReasonByUid, setRejectionReasonByUid] = useState({});

  async function handleReviewEligibility(userId, action) {
    setWorkingUid(userId);
    setFeedbackByUid((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });

    const rejectionReason = String(rejectionReasonByUid[userId] || "").trim();

    if (action === "reject" && !rejectionReason) {
      setFeedbackByUid((prev) => ({
        ...prev,
        [userId]: { text: t("admin.withdrawals.enter_rejection_reason", { defaultValue: "Enter a rejection reason." }), ok: false },
      }));
      setWorkingUid(null);
      return;
    }

    try {
      const fn = httpsCallable(functions, "reviewWithdrawEligibility");
      if (action === "approve") {
        await fn({ uid: userId, action: "approve" });
      } else {
        await fn({
          uid: userId,
          action: "reject",
          reason: rejectionReason,
        });
      }

      setFeedbackByUid((prev) => ({
        ...prev,
        [userId]: {
          text:
            action === "approve"
              ? "Eligibility approved."
              : "Eligibility rejected.",
          ok: true,
        },
      }));
      if (action === "reject") {
        setRejectionReasonByUid((prev) => ({ ...prev, [userId]: "" }));
      }
    } catch (error) {
      console.error("reviewWithdrawEligibility failed:", error);
      setFeedbackByUid((prev) => ({
        ...prev,
        [userId]: { text: callableErrorMessage(error), ok: false },
      }));
    } finally {
      setWorkingUid(null);
    }
  }

  useEffect(() => {
    const usersRef = collection(db, "users");

    const unsub = onSnapshot(
      usersRef,
      (snap) => {
        const list = snap.docs
          .map((docSnap) => ({ uid: docSnap.id, ...(docSnap.data() || {}) }))
          .filter((user) => {
            const gender = String(user?.gender || "").trim().toLowerCase();
            const verificationStatus = String(
              user?.verificationStatus || ""
            )
              .trim()
              .toLowerCase();
            const availableTokens = toNumber(user?.earnings?.availableTokens, 0);

            return (
              gender === "female" &&
              verificationStatus === "approved" &&
              availableTokens >= MIN_WITHDRAW_TOKENS
            );
          })
          .map((user) => {
            const displayName =
              String(user.displayName || user.name || "").trim() || "—";
            const docId = user.uid;
            return {
              id: docId,
              uid: docId,
              displayName,
              profileImage: pickPhotoUrl(user),
              gender: String(user.gender || "—"),
              displayCountry: String(user.displayCountry || "—"),
              realCountry: String(user.realCountry || "—"),
              femaleLevel:
                user.femaleLevel != null && user.femaleLevel !== ""
                  ? String(user.femaleLevel)
                  : "—",
              availableTokens: toNumber(user?.earnings?.availableTokens, 0),
              lifetimeTokens: toNumber(user?.earnings?.lifetimeTokens, 0),
              verificationStatus: String(user.verificationStatus || "—"),
              withdrawEligibilityStatus: String(
                user?.withdrawEligibility?.status || "—"
              ),
            };
          })
          .sort((a, b) => b.availableTokens - a.availableTokens);

        setUsers(list);
        setLoading(false);
      },
      (error) => {
        console.error("WithdrawEligibilityPage snapshot error:", error);
        setUsers([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const nf = new Intl.NumberFormat();

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.withdrawals.eligibility_title", { defaultValue: "Withdraw Eligibility" })}</h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.withdrawals.eligibility_subtitle_before_tokens", { defaultValue: "Female users approved for verification with at least" })}{" "}
          {MIN_WITHDRAW_TOKENS} {t("tokens.tokens", { defaultValue: "tokens" })}.
        </p>
      </header>

      {loading ? (
        <div className="admin1-state-card">{t("admin.withdrawals.loading_eligible_users", { defaultValue: "Loading eligible users..." })}</div>
      ) : users.length === 0 ? (
        <div className="admin1-state-card">{t("admin.withdrawals.no_eligible_users", { defaultValue: "No eligible users" })}</div>
      ) : (
        <section style={GRID}>
          {users.map((user) => {
            const busy = workingUid === user.id;
            const rejectionReason = String(
              rejectionReasonByUid[user.id] || ""
            ).trim();
            const rejectDisabled = busy || !rejectionReason;

            return (
            <article key={user.id} style={CARD}>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 12,
                    overflow: "hidden",
                    flexShrink: 0,
                    border: "1px solid #253654",
                    background: "#0f1a2f",
                  }}
                >
                  <ResolvedAvatar
                    raw={user.profileImage}
                    gender={user.gender}
                    imgStyle={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: "#f3f6ff",
                      marginBottom: 4,
                    }}
                  >
                    {user.displayName}
                  </div>
                  <div style={{ ...MONO, marginBottom: 4 }}>{user.uid}</div>
                  <div style={{ fontSize: 13, color: "#b8c8e8" }}>
                    {user.gender} · {user.displayCountry}
                    {user.realCountry && user.realCountry !== user.displayCountry
                      ? ` · real: ${user.realCountry}`
                      : null}
                  </div>
                </div>
              </div>

              <div style={SECTION_LABEL}>{t("admin.users.account", { defaultValue: "Account" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.users.female_level", { defaultValue: "Female level" })}</span>
                <span style={VALUE}>{user.femaleLevel}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.verification.title", { defaultValue: "Verification" })}</span>
                <span style={VALUE}>{user.verificationStatus}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.withdrawals.withdraw_eligibility", { defaultValue: "Withdraw eligibility" })}</span>
                <span style={VALUE}>{user.withdrawEligibilityStatus}</span>
              </div>

              <div style={{ ...SECTION_LABEL, marginTop: 14 }}>{t("admin.finance.earnings", { defaultValue: "Earnings" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.finance.available_tokens", { defaultValue: "availableTokens" })}</span>
                <span style={{ ...VALUE, fontWeight: 700 }}>
                  {nf.format(user.availableTokens)}
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.finance.lifetime_tokens", { defaultValue: "lifetimeTokens" })}</span>
                <span style={VALUE}>{nf.format(user.lifetimeTokens)}</span>
              </div>

              <div className="admin1-card__title" style={{ marginTop: 14 }}>
                {t("admin.withdrawals.rejection_reason_required", { defaultValue: "Rejection reason (required to reject)" })}
              </div>
              <input
                type="text"
                value={rejectionReasonByUid[user.id] ?? ""}
                onChange={(e) =>
                  setRejectionReasonByUid((prev) => ({
                    ...prev,
                    [user.id]: e.target.value,
                  }))
                }
                placeholder={t("admin.withdrawals.reject_reason_placeholder", { defaultValue: "Reason required to reject..." })}
                disabled={busy}
                style={{
                  width: "100%",
                  marginTop: 6,
                  background: "#0f1a2f",
                  border: "1px solid #253654",
                  color: "#d7def0",
                  borderRadius: 8,
                  padding: "9px 11px",
                  fontSize: 12,
                }}
              />

              {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: original approve
                  action preserved, disabled while real-money payouts are
                  disabled (backend also rejects "approve" independently). */}
              <button
                type="button"
                onClick={() => handleReviewEligibility(user.id, "approve")}
                disabled={busy || !ENABLE_REAL_MONEY_PAYOUTS}
                title={
                  ENABLE_REAL_MONEY_PAYOUTS
                    ? undefined
                    : "Real-money payouts are currently disabled."
                }
                style={{
                  marginTop: 12,
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 10,
                  border: "1px solid #37538f",
                  background: busy || !ENABLE_REAL_MONEY_PAYOUTS
                      ? "#1a2740"
                      : "linear-gradient(90deg, #4a6fb8, #37538f)",
                  color: "#f3f6ff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: busy || !ENABLE_REAL_MONEY_PAYOUTS ? "not-allowed" : "pointer",
                  opacity: busy || !ENABLE_REAL_MONEY_PAYOUTS ? 0.7 : 1,
                }}
              >
                {!ENABLE_REAL_MONEY_PAYOUTS
                  ? "Payouts disabled"
                  : busy
                  ? "Working…"
                  : "Approve eligibility"}
              </button>

              <button
                type="button"
                onClick={() => handleReviewEligibility(user.id, "reject")}
                disabled={rejectDisabled}
                style={{
                  marginTop: 8,
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 10,
                  border: "1px solid #7f1d1d",
                  background: rejectDisabled ? "#2a1515" : "#3f1515",
                  color: "#fecaca",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: rejectDisabled ? "not-allowed" : "pointer",
                  opacity: rejectDisabled ? 0.5 : 1,
                }}
              >
                Reject eligibility
              </button>

              {feedbackByUid[user.id] ? (
                <div
                  className="admin1-card__note"
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    color: feedbackByUid[user.id].ok ? "#86efac" : "#fca5a5",
                  }}
                >
                  {feedbackByUid[user.id].text}
                </div>
              ) : null}
            </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
